'use client';

import { useEffect, useState, useCallback } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import DirectoryClaimListingEditor from './DirectoryClaimListingEditor';
import ClaimSlugPicker from './ClaimSlugPicker';
import ClaimVerificationPanel from './ClaimVerificationPanel';
import {
  Container,
  Card,
  Text,
  Title,
  Button,
  Alert,
  Stack,
  ThemeIcon,
  Center,
  Group,
  Grid,
  Loader,
  Badge,
  Divider,
  TextInput,
  PasswordInput,
  PinInput,
  List,
} from '@mantine/core';
import {
  IconCheck,
  IconAlertCircle,
  IconMapPin,
  IconPhone,
  IconClock,
  IconArrowLeft,
  IconArrowRight,
  IconShoppingCart,
  IconShieldCheck,
  IconSparkles,
  IconTag,
  IconPencil,
  IconUser,
  IconMail,
} from '@tabler/icons-react';
import { getCategoryUrl } from '@/utils/slug';
import directoryClaimPublicService, {
  DirectoryClaimSummary,
  DirectoryClaimAcceptResult,
  OwnerClaimVerification,
} from '@/services/DirectoryClaimPublicService';
import directoryPresenceUpgradeService from '@/services/DirectoryPresenceUpgradeService';
import type { UpgradeSolution, UpgradeTierOption } from '@/services/DirectoryPresenceUpgradeService';
import { CONNECT_CONNECTIVE_LINE, CONNECT_GATE_COPY, FALLBACK_PRESENCE_MODES, MODE_UNLOCK_COPY, PRESENCE_ADDONS, SOLUTION_SETTINGS_PATH, sortPresenceModes } from '@/lib/presence-modes';
import AddonsStrip from '@/components/AddonsStrip';
import customerAuthService from '@/services/CustomerAuthService';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';

type PageState = 'loading' | 'valid' | 'expired' | 'claimed' | 'rejected' | 'invalid' | 'misaligned' | 'success' | 'otp_sent' | 'email_sent' | 'pending_approval';

/**
 * Best-effort platform (Auth0) session detection for success-screen CTAs.
 * Auth0's real session cookie (appSession) is httpOnly and invisible to
 * document.cookie; the SDK sets non-httpOnly auth0_email / auth0_id marker
 * cookies at login (lib/auth0.ts onCallback). A false negative is benign —
 * /auth/login bounces an authenticated user straight back to the returnTo
 * target (see .devin/skills/fix-auth0-redirect-loop.md).
 */
function detectPlatformSession(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    document.cookie.includes('auth0_email=') ||
    document.cookie.includes('auth0_id=') ||
    document.cookie.includes('auth0.')
  );
}

export default function DirectoryClaimClient() {
  const params = useParams();
  const router = useRouter();
  const { customer, refreshCustomer } = useCustomerAuth();
  const token = (params?.token as string) || '';

  const [state, setState] = useState<PageState>('loading');
  const [summary, setSummary] = useState<DirectoryClaimSummary | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [initiating, setInitiating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [claimResult, setClaimResult] = useState<DirectoryClaimAcceptResult | null>(null);
  // Best-effort platform (Auth0) session presence — drives the session-aware
  // success-screen CTAs (see detectPlatformSession).
  const [hasPlatformSession, setHasPlatformSession] = useState(false);

  // Claim-time owner verification (migration 274) — the consent gate. Null
  // until the owner checks the confirmation box; required to submit.
  const [verification, setVerification] = useState<OwnerClaimVerification | null>(null);

  // Optional pre-claim listing corrections (address/phone/hours/etc.) — the
  // same optional-submission pattern as the post-submit review screen.
  const [showListingEditor, setShowListingEditor] = useState(false);

  // Claimant verification fields (for operator-approval claims)
  const [claimantFirstName, setClaimantFirstName] = useState('');
  const [claimantLastName, setClaimantLastName] = useState('');
  const [claimantPhone, setClaimantPhone] = useState('');
  const [claimantBusinessAddress, setClaimantBusinessAddress] = useState('');

  // Proof attachments (uploaded after claim submission, while pending)
  const [uploadedProofs, setUploadedProofs] = useState<Array<{
    attachmentId: string; fileName: string; fileType: string; fileSize: number;
  }>>([]);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [proofError, setProofError] = useState<string | null>(null);

  // Password setup for promoted OAuth-only customers
  const [platformPassword, setPlatformPassword] = useState('');
  const [platformPasswordConfirm, setPlatformPasswordConfirm] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    if (!token) {
      setState('invalid');
      return;
    }
    try {
      const s = await directoryClaimPublicService.getClaimSummary(token);
      if (!s) {
        setState('invalid');
        return;
      }
      setSummary(s);
      if (s.claimRequestStatus === 'rejected') {
        // Rejection consumes the token — check before isConsumed so the owner
        // sees "not approved" rather than the misleading Already Claimed.
        setState('rejected');
      } else if (s.isConsumed) {
        setState('claimed');
      } else if (s.isExpired) {
        setState('expired');
      } else if (s.claimRequestStatus === 'pending') {
        // Claimant revisiting while under review — restore the pending screen
        // (proof upload + corrections) instead of the submit form.
        setState('pending_approval');
      } else if (s.claimEmailVerificationPending) {
        // Claimant revisiting before clicking their emailed confirmation
        // link (migration 358) — restore the "check your email" screen.
        setSentTo(s.claimVerificationSentTo || null);
        setState('email_sent');
      } else if (s.seedFidelity === 'misaligned') {
        setState('misaligned');
      } else {
        setState('valid');
      }
    } catch {
      setState('invalid');
    }
  }, [token]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Prefill claimant contact fields from the customer session — the data is
  // already on the account; don't make the owner retype it. Only fills
  // fields the owner hasn't typed into.
  useEffect(() => {
    if (!customer) return;
    setClaimantFirstName((v) => v || customer.firstName || '');
    setClaimantLastName((v) => v || customer.lastName || '');
    setClaimantPhone((v) => v || customer.phone || '');
  }, [customer]);

  // Refresh the teaser cards from the authenticated options endpoint when a
  // platform session exists (e.g., an existing platform user claiming from a
  // logged-in browser). Skipped otherwise — that endpoint requires an Auth0
  // session and the options embedded in the accept response are already
  // fresh (claim handoff spec — Data source).
  const refreshTenantId = claimResult?.tenantId;
  useEffect(() => {
    if (state !== 'success' || !hasPlatformSession || !refreshTenantId) return;
    let cancelled = false;
    (async () => {
      const opts = await directoryPresenceUpgradeService.getUpgradeOptions(refreshTenantId);
      if (!cancelled && opts && Array.isArray(opts.upgradeOptions) && opts.upgradeOptions.length > 0) {
        setClaimResult((prev) => prev ? {
          ...prev,
          currentTier: opts.currentTier,
          isGatewayUpgrade: opts.isGatewayUpgrade,
          upgradeOptions: opts.upgradeOptions,
          solutions: opts.solutions,
        } : prev);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state, hasPlatformSession, refreshTenantId]);

  const handleInitiate = async () => {
    setInitiating(true);
    setError(null);
    try {
      const result = await directoryClaimPublicService.initiateClaim(token, {
        claimantFirstName: claimantFirstName.trim() || undefined,
        claimantLastName: claimantLastName.trim() || undefined,
        claimantPhone: claimantPhone.trim() || undefined,
        claimantBusinessAddress: claimantBusinessAddress.trim() || undefined,
        // Pass customer identity from the frontend context as a fallback
        // in case the optionalCustomerAuth middleware doesn't parse the JWT
        // (PublicApiSingleton may not forward the Authorization header)
        customerEmail: customer?.email || undefined,
        customerId: customer?.id || undefined,
        // Owner verification consent gate (migration 274)
        verification: verification ?? undefined,
      });
      if (result.error) {
        setError(result.error);
      } else if (result.verificationRequired) {
        setSentTo(result.sentTo || null);
        setState('otp_sent');
      } else if (result.operatorApprovalRequired) {
        if (result.claimantVerificationRequired) {
          // Proof-of-inbox first (migration 358) — the request is only
          // filed once the emailed confirmation link is clicked.
          setSentTo(result.sentTo || null);
          setState('email_sent');
        } else {
          setState('pending_approval');
        }
      } else {
        // No verification required — proceed directly to accept
        await handleAccept();
      }
    } catch (err: any) {
      setError(err?.message || 'initiate_failed');
    } finally {
      setInitiating(false);
    }
  };

  const handleUploadProof = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadingProof(true);
    setProofError(null);
    try {
      for (const file of Array.from(files)) {
        const result = await directoryClaimPublicService.uploadProofAttachment(token, file);
        setUploadedProofs((prev) => [...prev, result]);
      }
    } catch (err: any) {
      setProofError(err?.message || 'Failed to upload file');
    } finally {
      setUploadingProof(false);
    }
  };

  const handleAccept = async (code?: string) => {
    setAccepting(true);
    setError(null);
    try {
      const result = await directoryClaimPublicService.acceptClaim(token, code);
      if (result.success) {
        await refreshCustomer();

        setHasPlatformSession(detectPlatformSession());
        setClaimResult(result);
        setState('success');
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'claim_completed', {
            event_category: 'claim',
          });
        }
      } else {
        setError(result.error || 'claim_failed');
        if (result.error === 'otp_max_attempts') {
          // Reset to valid state so user can re-initiate
          setOtpCode('');
          setState('valid');
        }
      }
    } catch (err: any) {
      setError(err?.message || 'claim_failed');
    } finally {
      setAccepting(false);
    }
  };

  const handleSetupPlatformPassword = async () => {
    setPasswordError(null);

    if (!platformPassword) {
      setPasswordError('Password is required');
      return;
    }
    if (platformPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    if (platformPassword !== platformPasswordConfirm) {
      setPasswordError('Passwords do not match');
      return;
    }

    setSettingPassword(true);
    try {
      const result = await customerAuthService.setupPlatformPassword(platformPassword);
      if (result.success && result.userTokens) {
        setPasswordSet(true);
        // Keep the platform tokens on claimResult so the success screen knows
        // a platform account exists. The tokens themselves are not consumed
        // by the web app — platform auth is Auth0 cookie-based (claim
        // handoff spec), so they are no longer written to localStorage.
        setClaimResult((prev) => prev ? {
          ...prev,
          requiresPasswordSetup: false,
          userTokens: result.userTokens,
        } : prev);
      } else {
        setPasswordError(result.error || 'Failed to set password');
      }
    } catch (err: any) {
      setPasswordError(err?.message || 'Failed to set password');
    } finally {
      setSettingPassword(false);
    }
  };

  const handleOtpSubmit = async () => {
    if (otpCode.length !== 6) {
      setError('Please enter the 6-digit code.');
      return;
    }
    await handleAccept(otpCode);
  };

  const handleResendOtp = async () => {
    setInitiating(true);
    setError(null);
    setOtpCode('');
    try {
      const result = await directoryClaimPublicService.initiateClaim(token);
      if (result.error) {
        setError(result.error);
      } else if (result.verificationRequired) {
        setSentTo(result.sentTo || null);
        setError(null);
      }
    } catch (err: any) {
      setError(err?.message || 'resend_failed');
    } finally {
      setInitiating(false);
    }
  };

  if (state === 'loading') {
    return (
      <Container size="sm" className="py-12">
        <Center>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading claim details...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  if (state === 'invalid') {
    return (
      <Container size="sm" className="py-12">
        <Card withBorder shadow="sm" padding="xl" radius="md">
          <Stack align="center" gap="md" ta="center">
            <ThemeIcon size={56} radius="xl" color="red">
              <IconAlertCircle size={28} />
            </ThemeIcon>
            <Title order={3}>Claim Link Not Found</Title>
            <Text c="dimmed">
              This claim link doesn&apos;t look right — it may have been removed. If you think
              that&apos;s a mistake, reach out and we&apos;ll get you a working link.
            </Text>
            <Button component={Link} href="/directory" variant="light" leftSection={<IconArrowLeft size={16} />}>
              Back to Directory
            </Button>
          </Stack>
        </Card>
      </Container>
    );
  }

  if (state === 'expired') {
    return (
      <Container size="sm" className="py-12">
        <Card withBorder shadow="sm" padding="xl" radius="md">
          <Stack align="center" gap="md" ta="center">
            <ThemeIcon size={56} radius="xl" color="orange">
              <IconClock size={28} />
            </ThemeIcon>
            <Title order={3}>Claim Link Expired</Title>
            <Text c="dimmed">
              This claim link has expired — they&apos;re good for 90 days. Reach out and we&apos;ll
              send you a fresh one.
            </Text>
            <Button component={Link} href="/directory" variant="light" leftSection={<IconArrowLeft size={16} />}>
              Back to Directory
            </Button>
          </Stack>
        </Card>
      </Container>
    );
  }

  if (state === 'claimed') {
    return (
      <Container size="sm" className="py-12">
        <Card withBorder shadow="sm" padding="xl" radius="md">
          <Stack align="center" gap="md" ta="center">
            <ThemeIcon size={56} radius="xl" color="blue">
              <IconCheck size={28} />
            </ThemeIcon>
            <Title order={3}>Already Claimed</Title>
            <Text c="dimmed">
              Someone&apos;s already claimed this listing. If this is your business and that
              wasn&apos;t you, reach out — we&apos;ll get it sorted.
            </Text>
            <Group gap="sm" justify="center">
              {customer && (
                <Button component={Link} href="/account" variant="light" leftSection={<IconUser size={16} />}>
                  Go to My Account
                </Button>
              )}
              <Button component={Link} href="/directory" variant="light" leftSection={<IconArrowLeft size={16} />}>
                Back to Directory
              </Button>
            </Group>
          </Stack>
        </Card>
      </Container>
    );
  }

  if (state === 'rejected') {
    return (
      <Container size="sm" className="py-12">
        <Card withBorder shadow="sm" padding="xl" radius="md">
          <Stack align="center" gap="md" ta="center">
            <ThemeIcon size={56} radius="xl" color="red">
              <IconAlertCircle size={28} />
            </ThemeIcon>
            <Title order={3}>Claim Not Approved</Title>
            <Text c="dimmed">
              We reviewed the claim request for <strong>{summary?.businessName}</strong> and
              weren&apos;t able to verify ownership, so the listing stays unclaimed for now.
              If you think that&apos;s a mistake — or you have more proof of ownership,
              like a business license or utility bill — reach out and we&apos;ll take
              another look.
            </Text>
            <Group gap="sm" justify="center">
              <Button component={Link} href="/support" variant="light" leftSection={<IconShieldCheck size={16} />}>
                Contact Support
              </Button>
              {customer && (
                <Button component={Link} href="/account" variant="light" leftSection={<IconUser size={16} />}>
                  Go to My Account
                </Button>
              )}
              <Button component={Link} href="/directory" variant="subtle" leftSection={<IconArrowLeft size={16} />}>
                Back to Directory
              </Button>
            </Group>
          </Stack>
        </Card>
      </Container>
    );
  }

  if (state === 'misaligned') {
    return (
      <Container size="sm" className="py-12">
        <Card withBorder shadow="sm" padding="xl" radius="md">
          <Stack align="center" gap="md" ta="center">
            <ThemeIcon size={56} radius="xl" color="orange">
              <IconAlertCircle size={28} />
            </ThemeIcon>
            <Title order={3}>Listing Under Review</Title>
            <Text c="dimmed">
              We&apos;re updating some details on this listing before it can be claimed. If this is
              your business, reach out and we&apos;ll get you set up.
            </Text>
            <Button component={Link} href="/directory" variant="light" leftSection={<IconArrowLeft size={16} />}>
              Back to Directory
            </Button>
          </Stack>
        </Card>
      </Container>
    );
  }

  if (state === 'otp_sent') {
    return (
      <Container size="sm" className="py-12">
        <Card withBorder shadow="sm" padding="xl" radius="md">
          <Stack align="center" gap="md" ta="center">
            <ThemeIcon size={56} radius="xl" color="blue">
              <IconShieldCheck size={28} />
            </ThemeIcon>
            <Title order={3}>Verify Your Identity</Title>
            <Text c="dimmed">
              We sent a 6-digit verification code
              {sentTo ? ` to ${sentTo}` : ''}. Enter it below to complete your claim
              for <strong>{summary?.businessName}</strong>.
            </Text>

            <Stack align="center" gap="md" w="100%">
              <PinInput
                length={6}
                value={otpCode}
                onChange={setOtpCode}
                size="lg"
                gap="xs"
                type="number"
                autoFocus
              />

              {error && (
                <Alert color="red" variant="light" w="100%">
                  {error === 'invalid_otp'
                    ? 'Incorrect code. Please try again.'
                    : error === 'otp_expired'
                      ? 'This code has expired. Please request a new one.'
                      : error === 'otp_max_attempts'
                        ? 'Too many attempts. Please request a new code.'
                        : error}
                </Alert>
              )}

              <Button
                size="md"
                loading={accepting}
                onClick={handleOtpSubmit}
                leftSection={<IconCheck size={18} />}
                disabled={otpCode.length !== 6}
              >
                Verify &amp; Claim
              </Button>

              <Button
                variant="subtle"
                size="xs"
                onClick={handleResendOtp}
                loading={initiating}
              >
                Resend code
              </Button>

              <Button
                component={Link}
                href="/directory"
                variant="subtle"
                size="xs"
                leftSection={<IconArrowLeft size={14} />}
              >
                Back to Directory
              </Button>
            </Stack>
          </Stack>
        </Card>
      </Container>
    );
  }

  if (state === 'email_sent') {
    return (
      <Container size="sm" className="py-12">
        <Card withBorder shadow="sm" padding="xl" radius="md">
          <Stack align="center" gap="md" ta="center">
            <ThemeIcon size={56} radius="xl" color="blue">
              <IconMail size={28} />
            </ThemeIcon>
            <Title order={3}>Check your email</Title>
            <Text c="dimmed">
              We sent a confirmation link{sentTo ? ` to ${sentTo}` : ' to your email'}.
              Click it to submit your claim for <strong>{summary?.businessName}</strong> —
              our team then reviews it, usually within 1–2 business days.
            </Text>
            <Text size="xs" c="dimmed">
              The link expires in 24 hours. Didn&apos;t get it? Check spam, or resend below.
            </Text>

            {error && (
              <Alert color="red" variant="light" w="100%">
                {error === 'rate_limited'
                  ? 'Too many attempts — please wait a moment and try again.'
                  : error === 'email_send_failed'
                    ? 'We could not send the confirmation email — please try again.'
                    : error}
              </Alert>
            )}

            <Button
              variant="subtle"
              size="xs"
              onClick={handleInitiate}
              loading={initiating}
            >
              Resend confirmation email
            </Button>

            <Button
              component={Link}
              href="/directory"
              variant="subtle"
              size="xs"
              leftSection={<IconArrowLeft size={14} />}
            >
              Back to Directory
            </Button>
          </Stack>
        </Card>
      </Container>
    );
  }

  if (state === 'pending_approval') {
    return (
      <Container size="sm" className="py-12">
        <Card withBorder shadow="sm" padding="xl" radius="md">
          <Stack gap="md">
            <Stack align="center" gap="md" ta="center">
              <ThemeIcon size={56} radius="xl" color="orange">
                <IconClock size={28} />
              </ThemeIcon>
              <Title order={3}>Claim Submitted for Review</Title>
              <Text c="dimmed">
                Your claim request for <strong>{summary?.businessName}</strong> has been
                submitted. Our team will review it and contact you within 1-2 business days
                to verify ownership.
              </Text>
            </Stack>

            <Divider />

            {/* Proof of ownership upload */}
            <Stack gap="sm">
              <Text size="sm" fw={500}>
                Speed up approval — upload proof of ownership
              </Text>
              <Text size="xs" c="dimmed">
                Provide any of the following to help our team verify you are the business owner.
                Accepted formats: PDF, PNG, JPEG.
              </Text>
              <List size="sm" c="dimmed" spacing={4}>
                <List.Item>Business license or permit</List.Item>
                <List.Item>Utility bill matching the business address</List.Item>
                <List.Item>Photo of the business storefront / interior</List.Item>
                <List.Item>Government-issued ID matching the claimant name</List.Item>
              </List>

              {uploadedProofs.length > 0 && (
                <Stack gap="xs" mt="xs">
                  {uploadedProofs.map((att) => (
                    <Group key={att.attachmentId} gap="xs" align="center">
                      <ThemeIcon size={24} radius="xl" color="green" variant="light">
                        <IconCheck size={14} />
                      </ThemeIcon>
                      <Text size="sm">
                        {att.fileName}{' '}
                        <Text size="xs" c="dimmed" span>
                          ({att.fileType}, {(att.fileSize / 1024).toFixed(0)}KB)
                        </Text>
                      </Text>
                    </Group>
                  ))}
                </Stack>
              )}

              {proofError && (
                <Text size="xs" c="red">{proofError}</Text>
              )}

              <Group gap="sm">
                <label>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => handleUploadProof(e.target.files)}
                    disabled={uploadingProof}
                  />
                  <Button
                    component="span"
                     variant="gradient" style={{ color: 'white' }} 
                    size="sm"
                    loading={uploadingProof}
                    leftSection={<IconShieldCheck size={16} />}
                  >
                    Upload Proof Document
                  </Button>
                </label>
                {uploadedProofs.length > 0 && (
                  <Text size="xs" c="green">
                    {uploadedProofs.length} file{uploadedProofs.length !== 1 ? 's' : ''} uploaded
                  </Text>
                )}
              </Group>
            </Stack>

            {summary && (
              <Stack gap="md">
                <ClaimSlugPicker
                  token={token}
                  currentSlug={summary.slug}
                  onSaved={loadSummary}
                />
                <Divider />
                <DirectoryClaimListingEditor
                  token={token}
                  summary={summary}
                  onSaved={loadSummary}
                />
              </Stack>
            )}

            <Divider />

            <Stack align="center" gap="sm" ta="center">
              <Text size="sm" c="dimmed">
                You can track your claim and access your account dashboard while you wait.
                Once approved, your Google Business Profile tools will appear there.
              </Text>
              <Group gap="sm">
                <Button component={Link} href="/account" variant="gradient" style={{ color: 'white' }} rightSection={<IconArrowRight size={16} />}>
                  Go to My Account
                </Button>
                <Button component={Link} href="/directory"  variant="gradient" style={{ color: 'white' }}  leftSection={<IconArrowLeft size={16} />}>
                  Back to Directory
                </Button>
              </Group>
            </Stack>
          </Stack>
        </Card>
      </Container>
    );
  }

  if (state === 'success') {
    const tenantId = claimResult?.tenantId;
    const needsPasswordSetup = claimResult?.requiresPasswordSetup && !passwordSet;
    const slug = summary?.slug;
    const shelfCategories = [summary?.category, ...(summary?.secondaryCategories || [])]
      .filter(Boolean) as string[];

    // Session-aware hrefs (claim handoff spec): /t/{tenantId}/* pages are
    // server-gated on a platform (Auth0) session. A freshly-claimed owner
    // usually has none (customer JWT only), so route through /auth/login
    // preserving the destination — the /t/[tenantId] layout hardcodes its
    // own returnTo to the dashboard and would drop the upgrade context.
    const upgradePath = tenantId
      ? `/t/${tenantId}/settings/subscription/upgrade?src=claim_success`
      : null;
    const dashboardPath = tenantId ? `/t/${tenantId}/dashboard` : '/account';
    const withReturnTo = (path: string) => `/auth/login?returnTo=${encodeURIComponent(path)}`;
    const upgradeHref = upgradePath
      ? (hasPlatformSession ? upgradePath : withReturnTo(upgradePath))
      : null;
    const dashboardHref = hasPlatformSession ? dashboardPath : withReturnTo(dashboardPath);
    // Same session-aware routing for the POS connect links — a freshly
    // claimed owner usually has no platform session yet.
    const solutionHref = (key: string) => {
      if (!tenantId) return null;
      const p = `/t/${tenantId}${SOLUTION_SETTINGS_PATH[key] ?? '/settings/integrations'}`;
      return hasPlatformSession ? p : withReturnTo(p);
    };

    return (
      <Container size="lg" className="py-12">
        <Card withBorder shadow="sm" padding="xl" radius="md">
          <Stack gap="lg">
            <Stack align="center" gap="md" ta="center">
              <ThemeIcon size={56} radius="xl" color="green">
                <IconCheck size={28} />
              </ThemeIcon>
              <Title order={3}>You own the listing for {summary?.businessName}</Title>
              <Text>
                Your free directory listing is live and shoppers can already find you. Choose a
                Presence Mode when you&apos;re ready for the full directory entry.
              </Text>
            </Stack>

            {/* Password setup for OAuth-only customers who were promoted */}
            {needsPasswordSetup && (
              <>
                <Alert color="orange" variant="light" icon={<IconShieldCheck size={16} />} w="100%">
                  <Text size="sm">
                    Your business owner account was created from your customer
                    profile. Set a password now to secure your dashboard access.
                    You can also use Google sign-in on the login page.
                  </Text>
                </Alert>

                <Stack gap="sm" w="100%">
                  <PasswordInput
                    label="Create Dashboard Password"
                    placeholder="Minimum 8 characters"
                    value={platformPassword}
                    onChange={(e) => setPlatformPassword(e.target.value)}
                    disabled={settingPassword}
                  />
                  <PasswordInput
                    label="Confirm Password"
                    placeholder="Re-enter your password"
                    value={platformPasswordConfirm}
                    onChange={(e) => setPlatformPasswordConfirm(e.target.value)}
                    disabled={settingPassword}
                    error={passwordError || undefined}
                  />
                  <Button
                    onClick={handleSetupPlatformPassword}
                    loading={settingPassword}
                    leftSection={<IconShieldCheck size={16} />}
                    fullWidth
                  >
                    Set Password & Continue
                  </Button>
                </Stack>
              </>
            )}

            {/* Show after password is set (or if it was never needed) */}
            {!needsPasswordSetup && (
              <>
                {passwordSet && (
                  <Alert color="green" variant="light" icon={<IconCheck size={16} />} w="100%">
                    <Text size="sm">
                      Your dashboard password is set. You can now sign in at
                      the login page with your email and this password.
                    </Text>
                  </Alert>
                )}

                {/* Entry Presence mode teaser (claim handoff spec §2/§4) */}
                <ClaimUpgradeTeaser
                  options={claimResult?.upgradeOptions}
                  solutions={claimResult?.solutions}
                  solutionHref={solutionHref}
                  connectHref={
                    tenantId
                      ? hasPlatformSession
                        ? `/t/${tenantId}/items/create`
                        : withReturnTo(`/t/${tenantId}/items/create`)
                      : null
                  }
                />

                {/* Directory Presence product allowance + entry step (E5, spec B-4) */}
                <Alert color="blue" variant="light" icon={<IconShoppingCart size={16} />} w="100%">
                  <Stack gap="xs">
                    <Text size="sm" fw={500}>
                      Your Directory Presence listing includes 5 free product slots
                    </Text>
                    <Text size="xs" c="dimmed">
                      Add up to 5 signature products to your storefront — free with your claimed
                      listing. Products on the free listing are display-only (no checkout).
                      It&apos;s enough to showcase your best offerings before upgrading to
                      Discovery (75 products) or Storefront (200 products).{' '}
                      <Link href="/place/about" style={{ color: 'inherit', textDecoration: 'underline' }}>
                        Learn more
                      </Link>
                    </Text>
                    {tenantId && (
                      <Button
                        component={Link}
                        href={hasPlatformSession ? `/t/${tenantId}/items/create` : withReturnTo(`/t/${tenantId}/items/create`)}
                        size="xs"
                        variant="light"
                        mt="xs"
                        style={{ alignSelf: 'flex-start' }}
                      >
                        Add your first product
                      </Button>
                    )}
                  </Stack>
                </Alert>

                {/* Multi-shelf confirmation — the claimed listing appears on every
                    matching category shelf (multi-category shelf placement spec §3.3) */}
                {(summary?.secondaryCategories?.length ?? 0) > 0 && (
                  <Alert color="teal" variant="light" icon={<IconTag size={16} />} w="100%">
                    <Stack gap="xs">
                      <Text size="sm" fw={500}>
                        Your listing appears on {shelfCategories.length} category shelves
                      </Text>
                      <Group gap="xs">
                        {shelfCategories.map((cat) => (
                          <Badge key={cat} color="teal" variant="light">
                            <Link href={getCategoryUrl({ name: cat })} style={{ color: 'inherit', textDecoration: 'none' }}>
                              {cat}
                            </Link>
                          </Badge>
                        ))}
                      </Group>
                      <Text size="xs" c="dimmed">
                        Each shelf is a separate browse page customers use to find businesses like yours.
                      </Text>
                    </Stack>
                  </Alert>
                )}

                <Group justify="center">
                  {upgradeHref && (
                    <Button component={Link} href={upgradeHref} variant="light" leftSection={<IconSparkles size={16} />}>
                      See upgrade options
                    </Button>
                  )}
                  <Button
                    component={Link}
                    href={dashboardHref}
                    variant="light"
                    leftSection={<IconCheck size={16} />}
                  >
                    Go to Dashboard
                  </Button>
                  {slug && (
                    <Button
                      component={Link}
                      href={`/directory/${slug}`}
                      variant="light"
                      leftSection={<IconMapPin size={16} />}
                    >
                      View Listing
                    </Button>
                  )}
                  <Button
                    component={Link}
                    href="/directory"
                    variant="subtle"
                    leftSection={<IconArrowLeft size={16} />}
                  >
                    Back to Directory
                  </Button>
                </Group>

                <Text size="xs" c="dimmed" ta="center">
                  You&apos;ll sign in with your account email. Your dashboard is at{' '}
                  <strong>{tenantId ? `/t/${tenantId}/dashboard` : 'your account'}</strong>.
                </Text>
              </>
            )}
          </Stack>
        </Card>
      </Container>
    );
  }

  // state === 'valid'
  return (
    <Container size="sm" className="py-12">
      <Card withBorder shadow="sm" padding="xl" radius="md">
        <Stack gap="lg">
          {/* Header */}
          <div>
            <Badge color="green" variant="light" mb="sm">
              Unclaimed Directory Listing
            </Badge>
            <Title order={2}>{summary?.businessName}</Title>
            <Text c="dimmed" size="sm" mt="xs">
              {summary?.category} · {summary?.city}, {summary?.state}
            </Text>
            {(summary?.secondaryCategories?.length ?? 0) > 0 && (
              <>
                <Group gap="xs" mt="xs">
                  {(summary?.secondaryCategories || []).map((cat) => (
                    <Badge key={cat} color="gray" variant="light" size="sm">
                      {cat}
                    </Badge>
                  ))}
                </Group>
                <Text size="xs" c="dimmed" mt="xs">
                  Once claimed, your listing will appear on {(summary?.secondaryCategories?.length ?? 0) + 1} category
                  shelves: {[summary?.category, ...(summary?.secondaryCategories || [])].filter(Boolean).join(' · ')}.
                </Text>
              </>
            )}
          </div>

          <Divider />

          {/* Listing details */}
          <Stack gap="sm">
            <Group gap="xs">
              <ThemeIcon size={20} radius="xl" color="gray" variant="light">
                <IconMapPin size={14} />
              </ThemeIcon>
              <Text size="sm">{summary?.address}</Text>
            </Group>
            {summary?.phone && (
              <Group gap="xs">
                <ThemeIcon size={20} radius="xl" color="gray" variant="light">
                  <IconPhone size={14} />
                </ThemeIcon>
                <Text size="sm">{summary.phone}</Text>
              </Group>
            )}
            {summary?.snapEbtReported && (
              <Group gap="xs">
                <ThemeIcon size={20} radius="xl" color="green" variant="light">
                  <IconShoppingCart size={14} />
                </ThemeIcon>
                <Text size="sm" c="green.7">
                  SNAP/EBT reported
                </Text>
              </Group>
            )}
          </Stack>

          <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
            You&apos;re already listed on the {summary?.city} {summary?.category} directory from public
            information (address, phone, and SNAP where reported). Claim the listing to fix hours or
            phone, add a photo, and manage the categories it appears under — your listing can show up
            on every matching category shelf. Your shelves can also be browsed online, so customers can see
            what you carry before they visit.
          </Alert>

          {/* Public web address — the prospect picks their slug up front
              (address-aware patterns, auto-saved on select). Same write the
              corrections editor uses; kept out of the collapsed corrections
              block because the URL is an ownership choice, not a correction. */}
          {summary && (
            <>
              <ClaimSlugPicker
                token={token}
                currentSlug={summary.slug}
                onSaved={loadSummary}
              />
              <Divider />
            </>
          )}

          {/* Owner verification — required consent step (migration 274).
              The owner's confirmed categories + attributes are minted on the
              business at claim submit and flow through to the listing. */}
          {summary && (
            <div id="claim-verification">
              <ClaimVerificationPanel
                summary={summary}
                onChange={setVerification}
              />
            </div>
          )}

          {/* Optional pre-claim corrections — the same optional-submission
              pattern as the post-submit review screen (PUT /claim/:token/listing
              is token-gated, so edits apply before or after submit).
              includeCategories=false: the verification panel above owns the
              category consent contract, and its payload is minted at claim
              submit — a second category editor here would be overwritten. */}
          {summary && (
            <Stack gap="xs">
              <Button
                variant="subtle"
                size="sm"
                onClick={() => setShowListingEditor((v) => !v)}
                leftSection={<IconPencil size={14} />}
              >
                {showListingEditor
                  ? 'Hide listing corrections'
                  : 'Something wrong? Correct your listing details (optional)'}
              </Button>
              {showListingEditor && (
                <DirectoryClaimListingEditor
                  token={token}
                  summary={summary}
                  onSaved={loadSummary}
                  includeCategories={false}
                />
              )}
            </Stack>
          )}

          <Alert color="gray" variant="light" icon={<IconShieldCheck size={16} />}>
            <Text size="sm" fw={500} mb={4}>
              What happens after you claim
            </Text>
            <List size="sm" c="dimmed" spacing={4}>
              <List.Item>
                We may email you a 6-digit code or a confirmation link to prove it&apos;s
                you, or our team may review the claim — reviews usually take 1–2 business days.
              </List.Item>
              <List.Item>
                If your claim goes to review, the confirmation screen lets you upload proof
                of ownership — a business license, utility bill, or storefront photo — and
                keep correcting your listing details while you wait.
              </List.Item>
              <List.Item>
                Once approved, the listing is yours — manage hours, photos, and categories
                from your dashboard.
              </List.Item>
              <List.Item>
                Your listing is free forever — 5 free product slots to put your top
                sellers in front of nearby shoppers. That&apos;s our{' '}
                <Link href="/place/about" style={{ color: 'inherit', textDecoration: 'underline' }}>
                  promise
                </Link>
                .
              </List.Item>
            </List>
          </Alert>

          <Divider />

          {/* Claim action */}
          {customer ? (
            <Stack gap="sm">
              <Text size="sm" c="dimmed">
                Signed in as <strong>{customer.email}</strong>
              </Text>

              {/* Claimant verification info — used by operator to verify ownership */}
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Your contact information
                </Text>
                <Text size="xs" c="dimmed">
                  We use this to verify you are the business owner before approving your claim.
                </Text>
                <Group grow>
                  <TextInput
                    label="First name"
                    placeholder="First name"
                    value={claimantFirstName}
                    onChange={(e) => setClaimantFirstName(e.target.value)}
                    size="sm"
                  />
                  <TextInput
                    label="Last name"
                    placeholder="Last name"
                    value={claimantLastName}
                    onChange={(e) => setClaimantLastName(e.target.value)}
                    size="sm"
                  />
                </Group>
                <TextInput
                  label="Phone number"
                  placeholder="(555) 123-4567"
                  value={claimantPhone}
                  onChange={(e) => setClaimantPhone(e.target.value)}
                  size="sm"
                  leftSection={<IconPhone size={14} />}
                />
                <TextInput
                  label="Business address (optional)"
                  placeholder="123 Main St, Indianapolis, IN 46224"
                  value={claimantBusinessAddress}
                  onChange={(e) => setClaimantBusinessAddress(e.target.value)}
                  size="sm"
                  leftSection={<IconMapPin size={14} />}
                />
              </Stack>

              {error && (
                <Alert color="red" variant="light">
                  {error === 'authentication_required'
                    ? 'Please sign in to claim this listing.'
                    : error === 'already_claimed'
                      ? 'This listing has already been claimed.'
                      : error === 'verification_required' || error === 'verification_not_confirmed'
                        ? 'Please review and confirm your business info above before claiming.'
                        : error === 'claimant_email_required'
                          ? 'We need an email address on your account to send your confirmation link.'
                          : error === 'rate_limited'
                            ? 'Too many attempts — please wait a moment and try again.'
                            : error === 'email_send_failed'
                              ? 'We could not send the confirmation email — please try again.'
                              : 'Something went wrong. Please try again.'}
                </Alert>
              )}
              {!verification && (
                <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
                  <Group justify="space-between" align="center" gap="sm">
                    <Text size="sm">
                      One step left — check the confirmation box in{' '}
                      <strong>Verify your business info</strong> above to claim this listing.
                    </Text>
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      style={{ flexShrink: 0 }}
                      onClick={() =>
                        document
                          .getElementById('claim-verification')
                          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                      }
                    >
                      Take me there
                    </Button>
                  </Group>
                </Alert>
              )}
              <Button
                size="md"
                variant='gradient' style={{ color: 'white' }}
                loading={initiating || accepting}
                onClick={handleInitiate}
                disabled={!verification}
                leftSection={<IconCheck size={18} />}
              >
                Claim This Listing
              </Button>
            </Stack>
          ) : (
            <Stack gap="sm">
              <Text size="sm" c="dimmed">
                Create a free account or sign in to claim this listing.
              </Text>
              <Group>
                <Button
                component={Link}
                variant='gradient' style={{ color: 'white' }}
                href={`/customerlogin?redirect=${encodeURIComponent(`/place/claim/${token}`)}&mode=register`}
                size="md">
                  Create Account
                </Button>
                <Button
                  component={Link}
                  href={`/customerlogin?redirect=${encodeURIComponent(`/place/claim/${token}`)}&mode=login`}
                 variant='gradient' style={{ color: 'white' }}
                  size="md"
                >
                  Sign In
                </Button>
              </Group>
            </Stack>
          )}

          <Group justify="center" gap="xs">
            {summary?.slug && (
              <Button
                component={Link}
                href={`/place/${summary.slug}`}
                variant="subtle"
                size="xs"
                leftSection={<IconMapPin size={14} />}
              >
                View this listing
              </Button>
            )}
            {customer && (
              <Button
                component={Link}
                href="/account"
                variant="subtle"
                size="xs"
                leftSection={<IconUser size={14} />}
              >
                My Account
              </Button>
            )}
            <Button
              component={Link}
              href="/directory"
              variant="subtle"
              size="xs"
              leftSection={<IconArrowLeft size={14} />}
            >
              Back to Directory
            </Button>
          </Group>

          <Divider />

          <Text size="xs" c="dimmed" ta="center">
            Listed from public directories / SNAP / news. Not a claimed profile.
          </Text>
        </Stack>
      </Card>
    </Container>
  );
}

// ─── Claim upgrade teaser (claim handoff spec §2/§4) ──────────────────────
//
// Renders the three Entry Presence mode cards on the claim success screen.
// Live values come from the upgradeOptions embedded in the claim accept
// response (or the authenticated refresh when a platform session exists);
// the shared static fallback renders only when no options are available.
function ClaimUpgradeTeaser({ options, solutions, solutionHref, connectHref }: { options?: UpgradeTierOption[]; solutions?: UpgradeSolution[]; solutionHref?: (key: string) => string | null; connectHref?: string | null }) {
  const isFallback = !options || options.length === 0;
  const sorted = sortPresenceModes(isFallback ? FALLBACK_PRESENCE_MODES : options);
  // CONNECT_LADDER_COPY_SPEC §5 — the claim moment is always State A: a freshly
  // claimed seed has no inventory, so no connection signal is needed here.
  const [showModes, setShowModes] = useState(false);

  return (
    <Stack gap="xs">
      <Text ta="center" fw={700} size="lg">
        {CONNECT_GATE_COPY.stateA.heading}
      </Text>
      <Text ta="center" size="sm" c="dimmed">
        {CONNECT_GATE_COPY.stateA.body}
      </Text>
      {connectHref && (
        <Group justify="center" mt="xs">
          <Button component={Link} href={connectHref} leftSection={<IconShoppingCart size={16} />}>
            {CONNECT_GATE_COPY.stateA.cta}
          </Button>
        </Group>
      )}
      <Group justify="center">
        <Button variant="subtle" size="xs" onClick={() => setShowModes((v) => !v)}>
          {showModes ? 'Hide options' : CONNECT_GATE_COPY.stateA.secondary}
        </Button>
      </Group>

      {showModes && (
        <>
          <Text ta="center" fw={500} mt="sm">
            {CONNECT_CONNECTIVE_LINE}
          </Text>
          <Grid gap="md">
            {sorted.map((mode) => (
              <Grid.Col key={mode.tierKey} span={{ base: 12, md: 6, lg: 4 }}>
                <Card withBorder radius="md" padding="md" h="100%" style={{ minHeight: 220 }}>
                  <Stack gap="xs" align="center" ta="center">
                    <Group gap="xs" justify="center">
                      {mode.mode && (
                        <Badge variant="light" color={mode.isPrimary ? 'blue' : 'gray'}>
                          {mode.mode}
                        </Badge>
                      )}
                      {mode.isPrimary && (
                        <Text size="xs" fw={500} c="blue">
                          Recommended
                        </Text>
                      )}
                    </Group>
                    <Text fw={700} size="lg">
                      {mode.tagline || mode.displayName || mode.name}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {mode.displayName || mode.name} ·{' '}
                      {isFallback ? `from $${mode.priceMonthly}/mo` : `$${mode.priceMonthly}/mo`}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {MODE_UNLOCK_COPY[mode.tierKey] ?? ''}
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
          <Card withBorder radius="md" padding="sm" bg="gray.0">
            <Text size="sm" ta="center" fw={600}>
              Add-ons that run on what you already have
            </Text>
            <AddonsStrip items={PRESENCE_ADDONS} hrefFor={solutionHref} className="mt-2" />
            <Text size="xs" c="dimmed" ta="center" mt={6}>
              {PRESENCE_ADDONS[0]?.tagline} — every option can add these as add-ons.
            </Text>
          </Card>
          <Text size="xs" c="dimmed" ta="center">
            Every option can add the others&apos; capabilities — POS sync, coupons, funnels
            and social selling — as add-ons whenever you&apos;re ready. No plan switch, no rebuild.
          </Text>
        </>
      )}
    </Stack>
  );
}
